/**
 * BulkEditVisualNotesModal
 *
 * Modal for bulk editing visual notes across multiple products.
 */

import React, { useState } from 'react';
import {
    X,
    Sparkles,
    Loader2,
    ShoppingBag
} from 'lucide-react';

const BulkEditVisualNotesModal = ({
    isOpen,
    onClose,
    onSave,
    products = [],
    loading = false
}) => {
    const [visualNotes, setVisualNotes] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(visualNotes);
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: 'var(--space-4)'
            }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    position: 'relative'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-4)',
                    paddingBottom: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <Sparkles size={20} style={{ color: 'var(--color-primary)' }} />
                        <h3 style={{ margin: 0 }}>
                            Bulk Edit Visual Notes
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-1)' }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Selected Products Preview */}
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-4)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-2)'
                    }}>
                        <ShoppingBag size={14} style={{ color: 'var(--color-primary)' }} />
                        <span style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                            {products.length} product{products.length !== 1 ? 's' : ''} selected
                        </span>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-2)'
                    }}>
                        {products.slice(0, 5).map((product) => (
                            <div
                                key={product.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-1) var(--space-2)',
                                    background: 'var(--color-bg-primary)',
                                    borderRadius: 'var(--radius-full)',
                                    fontSize: 'var(--font-size-xs)',
                                    border: '1px solid var(--color-border)'
                                }}
                            >
                                {product.imageUrl && (
                                    <img
                                        src={product.imageUrl}
                                        alt=""
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: 'var(--radius-sm)',
                                            objectFit: 'cover'
                                        }}
                                    />
                                )}
                                <span style={{
                                    maxWidth: '100px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {product.name}
                                </span>
                            </div>
                        ))}
                        {products.length > 5 && (
                            <span style={{
                                padding: 'var(--space-1) var(--space-2)',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                +{products.length - 5} more
                            </span>
                        )}
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            Visual Notes
                        </label>
                        <textarea
                            value={visualNotes}
                            onChange={(e) => setVisualNotes(e.target.value)}
                            placeholder="Describe the visual appearance to apply to all selected products...

Example: Premium cookware with brushed stainless steel finish. Ergonomic handles with silicone grip. Professional-grade appearance with subtle logo engraving on handle."
                            className="input"
                            rows={6}
                            style={{
                                resize: 'vertical',
                                width: '100%',
                                minHeight: '150px'
                            }}
                            autoFocus
                        />
                        <p style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            margin: 'var(--space-2) 0 0 0'
                        }}>
                            This will replace any existing visual notes on the selected products.
                        </p>
                    </div>

                    {/* Footer */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 'var(--space-2)',
                        marginTop: 'var(--space-6)',
                        paddingTop: 'var(--space-4)',
                        borderTop: '1px solid var(--color-border)'
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-ghost"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || !visualNotes.trim()}
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={16} className="spin" />
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <Sparkles size={16} />
                                    Update {products.length} Product{products.length !== 1 ? 's' : ''}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BulkEditVisualNotesModal;
